import { useCallback, useEffect, useState } from "react";
import { DocumentsService } from "../../../services/document/documentsService";
import { RootState } from "../../../redux/store";
import { useSelector } from "react-redux";
import useHandleErrors from "../../../hooks/useHandleErrors";
import useDocuments from "../../../hooks/api-calls/useDocuments";
import { useFiltersPR } from "../../../components/providers/FiltersProvider";
import useFetch from "../../../hooks/useFetch";
import { toast } from "sonner";
import Page from "../../../layout/Page/Page";
import CustomTable from "../../../components/table/CustomTable";
import { ActionIcon, Tooltip } from "rizzui";
import { MdOutlineDownload, MdOutlineModeEditOutline } from "react-icons/md";
import DeletePopover from "../../../components/buttons/DeletePopover";
import { FilterDrawerView } from "../../../components/table/components/TableFilter";
import ReportFilters from "./ReportFilters";
import DocumentsFormModal from "../../docs/components/DocumentsFormModal";
import { ListApiResponse } from "../../../type/entities/apiResponse-type";
import moment from "moment";
import StatusDropdown from "../../../components/forms/StatusDropdown";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";

interface ReportListProps {
    doRefresh: boolean;
    setDoRefresh: (value: boolean) => void;
    isOpen: boolean;
    setIsOpen: (value: boolean) => void;
}

const columnOrder = [
    'id',
    'name',
    'category',
    'user',
    'client',
    'park',
    'description',
    'company',
    'betweenDates',
    'status',
    'uploadedAt',
    'document',
]

const ReportList: React.FC<ReportListProps> = ({ doRefresh, setDoRefresh, isOpen, setIsOpen }) => {

    // STATES
    
    const [ isAdmin, setIsAdmin ] = useState(false);
    const [ selectedDocument, setSelectedDocument ] = useState<any>(null);
    const [ openFilters, setOpenFilters ] = useState(false);
    const documentsService = new DocumentsService();


    // HOOKS 

    const userId = useSelector((state: RootState) => state.auth.user?.id);
    const roles = useSelector((state: RootState) => state.auth.user?.roles);
    const { userCan } = usePrivilege();
    const { handleErrors } = useHandleErrors();
    const { getDocumentStatusList } = useDocuments();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();
    

    // FETCH DATA

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES OBTIENE EL LISTADO DE DOCUMENTOS
     * @EN GETS THE LIST OF DOCUMENTS
     */
    // -------------------------------------------------------------------------------------------------------------
    const [data, loading, error, refetch ] = useFetch(useCallback(async () => {
        const response = await documentsService.listCustomReports(filters);
        return response.getResponseData();
    }, [filters, doRefresh]));
    // -------------------------------------------------------------------------------------------------------------


    // FUNCTIONS
    
    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA LA ELIMINACIÓN DE UN DOCUMENTO
     * @EN HANDLES THE DELETION OF A DOCUMENT
     * 
     * @param id
     */
    // -------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try{
            const response = await(await documentsService.deleteDocument(id)).getResponseData();
            if(response.success) {
                toast.success('documento eliminado correctamente');
                refetch();
            }else{
                handleErrors(response);
            }
        }catch(error) {
            handleErrors(error);
        }
    }
    // -------------------------------------------------------------------------------------------------------------


    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA LA ELIMINACION DE VARIAS PARADAS
     * @EN HANDLES THE DELETION OF MULTIPLE STOPS
     * 
     * @param ids
     */
    // -------------------------------------------------------------------------------------------------------------
    const handleDeleteMultiple = async (ids: string[]) => {
        try{
            const response = await(await documentsService.multiDeleteDocuments(ids)).getResponseData();
            if(response.success) {
                toast.success('Paradas eliminadas correctamente');
            }else{
                handleErrors(response);
            }
        }catch(error) {
            handleErrors(error);
        }
    }
    // -------------------------------------------------------------------------------------------------------------


    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES MUESTRA LOS DATOS EN EL ORDEN CORRECTO
     * @EN SHOWS THE DATA IN THE CORRECT ORDER
     * 
     */
    // -------------------------------------------------------------------------------------------------------------
    const orderedData = () => data?.data.map((row: any) => {
        const orderedRow: any = {};
        columnOrder.forEach((key) => {
            if (key === 'customId') {
                orderedRow[key] = row.id || '-';
            } else {
                orderedRow[key] = row[key];
            }
        });
        return orderedRow;
    });
    // -------------------------------------------------------------------------------------------------------------
    

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES CAMBIA EL ESTADO DE UN DOCUMENTO
     * @EN CHANGES THE STATUS OF A DOCUMENT
     * 
     * @param id
     * @param status
     */
    // -------------------------------------------------------------------------------------------------------------
    const changeStatus = async (id: string, status: string) => {
        try{
            const response = await(await documentsService.changeStatusDocument(id, status)).getResponseData();
            if(response.success) {
                toast.success('Estado del documento cambiado correctamente');
                refetch();
            }else{
                handleErrors(response);
            }
        }catch(error) {
            handleErrors(error);
        }
    }
    // -------------------------------------------------------------------------------------------------------------


    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES DESCARGAR DOCUMENTO
     * @EN DOWNLOAD DOCUMENT
     * 
     * @param id
     * @param extension
     */
    // -------------------------------------------------------------------------------------------------------------
    const downloadDocument = async (id: string, extension: string, name:string) => {
        try {
            const response = (await documentsService.downloadDocument(id)).getResponseData();
            const type = response.type;
            if (response) {
                const blob = new Blob([response], { type: type });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${name}.${extension}`;
                a.click();
                window.URL.revokeObjectURL(url);
                toast.success('Documento descargado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    }
    // -------------------------------------------------------------------------------------------------------------


    //-----------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CERRAR MODAL
     * @EN CLOSE MODAL
     */
    //-----------------------------------------------------------------------------------------------------------------------
    const onClose =  () => {
        setIsOpen(false);
        setTimeout(() => {  
            setSelectedDocument(null);
        }
        , 150);
    };
    //-----------------------------------------------------------------------------------------------------------------------


    // USE EFFECT

    useEffect(() => {
        if(!roles?.includes('Administrador') && !roles?.includes('Superadministrador')) {
            updateFilters({ user: userId });
            setIsAdmin(false);
        }else{
            setIsAdmin(true);
        }
    }, [roles]);


    // RENDER 

    return (

        <Page container='fluid'>
            <CustomTable
                id={'reports-table'}
                divClassName='max-h-table-report'
                classNameTable='fs-table'
                headerTableClassName='fs-table-header'
                columnOrder={columnOrder}
                data={orderedData()}
                isLoading={loading}
                columnsNotShown={['document', 'user', 'extension', 'documentType']}
                columnsNoSortables={[
                ]}
                isSortable
                overrideColumns={[
                    {
                        key: 'company',
                        permissions: [{ group: 'companies', permission: 'list' }],
                        render: (row: any) => {
                            return row.row.original.company?.name || 'N/A';
                        },
                    },
                    {
                        key: 'status',
                        customHeader: 'Estado',
                        render: (row: any) => {
                            const documentStatus = row.row.original?.status;
                            const documentId = row.row.original?.id;
                            return (
                                <div className='flex flex-col gap-2 justify-content-center text-center' key={documentId + documentStatus.id}>
                                    {(documentId && documentStatus) ? (
                                        <StatusDropdown
                                            optionsClassName="fs-table"
                                            selectedClassName="fs-table"
                                            divClassName="fs-table"
                                            entityId={row.row.original?.id}
                                            title="¿Estas seguro de cambiar el estado del informe?"
                                            statesOptions={getDocumentStatusList()}
                                            currentState={documentStatus}
                                            handleStateChange={(entityId: string, statusId: string) => changeStatus(documentId, statusId)}
                                            hasComments={false}
                                            disabled={row.row.original?.status?.name == 'Eliminado' || !userCan('edit', 'document')}
                                        />
                                    ) : "N/A"}
                                </div>
                            );
                        }
                    },
                    {
                        key: 'client',
                        render: (row: any) => {
                            return row.row.original.client?.name || 'Sin cliente';
                        },
                    },
                    {
                        key: 'park',
                        render: (row: any) => {
                            return row.row.original.park?.name || 'Sin parque';
                        },
                    },
                    {
                        key: 'uploadedAt',
                        customHeader: 'Generado el',
                        render: (row: any) => {
                            return row.row.original.uploadedAt ? moment(row.row.original.uploadedAt.date).format('DD/MM/YYYY') : 'N/A';
                        },
                    },
                    {
                        key: 'betweenDates',
                        customHeader: 'Rango de fechas',
                        render: (row: any) => {
                            return row.row.original.betweenDates
                        }
                    }
                    
                ]}
                actions={[
                    {
                        label: 'Download',
                        permissions: { group: 'document', permission: 'get' },
                        render: (row: any) => {
                            return (
                                row.status.name !== 'Eliminado' ? 
                                <Tooltip
                                    size="sm"
                                    content={'Descargar documento'}
                                    placement="top"
                                    color="invert"
                                >
                                    <ActionIcon
                                        as="span"
                                        size="sm"
                                        variant="outline"
                                        className="hover:!border-gray-900 hover:text-gray-700 bg-white cursor-pointer w-5 h-5"
                                        onClick={() => {
                                            downloadDocument(row?.id, row?.document.extension, row?.name);
                                        }}
                                    >
                                        <MdOutlineDownload className="h-3 w-3" size={20} />
                                    </ActionIcon>
                                </Tooltip>
                                : null
                            );
                        },
                    },
                    {
                        label: 'Edit',
                        permissions: { group: 'document', permission: 'edit' },
                        render: (row: any) => {
                            return (
                                <Tooltip
                                    size="sm"
                                    content={'Editar documento'}
                                    placement="top"
                                    color="invert"
                                >
                                    <ActionIcon
                                        as="span"
                                        size="sm"
                                        variant="outline"
                                        className="hover:!border-gray-900 hover:text-gray-700 bg-white w-5 h-5"
                                        onClick={() => {
                                            setIsOpen(true)
                                            setSelectedDocument(row.id);
                                        }}
                                    >
                                        <MdOutlineModeEditOutline className="h-3 w-3" size={20} />
                                    </ActionIcon>
                                </Tooltip>
                            );
                        },
                    },  
                    {
                        label: 'Delete',
                        permissions: { group: 'document', permission: 'delete' },
                        render: (row: any) => {
                            return (
                                row.status.name !== 'Eliminado' ?  
                                <Tooltip
                                    size="sm"
                                    content={'Eliminar documento'}
                                    placement="top"
                                    color="invert"
                                >
                                    <div>
                                        <DeletePopover
                                            actionIconClassName="w-5 h-5"
                                            title={`Eliminar documento`}
                                            description={`¿Estás seguro de que deseas eliminar la parada ${row.name}?`}
                                            onDelete={() => handleDelete(row.id)}
                                        />
                                    </div>
                                </Tooltip> : null
                            );
                        },
                    },
                ]}
                filters={filters}
                updateFilters={updateFilters}
                handleMultipleDelete={handleDeleteMultiple}
                updateFilterOrder={updateFilterOrder}
                defaultOrder={filters.filter_order || undefined}
                paginationData={{
                    pageSize: filters.limit,
                    currentPage: filters.page,
                    pageCount: (data as ListApiResponse) ? data.lastPage : 0,
                    totalCount: data?.totalRegisters,
                    handlePagination: updatePage,
                    handlePerPage: updatePageSize,
                }}
                toggleFilters={() => setOpenFilters(!openFilters)}
            />

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros de Informes'} hasSearched={false}>
                <ReportFilters filters={filters} updateFilters={updateFilters} resetFilters={resetFilters} />
            </FilterDrawerView>


            <DocumentsFormModal
                isOpen={isOpen}
                onClose={onClose}
                refetch={refetch}
                document={selectedDocument}
                isAdmin={isAdmin}
                userId={userId}
                report
            />

        </Page>
    )
}

export default ReportList;
